/**
 * SimpleCalc — Calculadora clásica (números + operaciones básicas).
 * Se muestra en un Popover desde el TopMenuBar.
 *
 * Soporta:
 *  - Suma, resta, multiplicación, división
 *  - Porcentaje, cambio de signo, borrar (AC / DEL)
 *  - Teclado físico (0-9, . , + - * / Enter, Backspace, Escape)
 *  - Historial de la última operación
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calculator, Delete, Clipboard, History, X, Copy } from "lucide-react";

const fmt = (n) => {
  if (!Number.isFinite(n)) return "Error";
  // Show up to 10 significant digits without scientific notation for common cases
  const abs = Math.abs(n);
  if (abs !== 0 && (abs >= 1e12 || abs < 1e-6)) return String(n);
  const s = String(Math.round(n * 1e10) / 1e10);
  const parts = s.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

const applyOp = (a, b, op) => {
  switch (op) {
    case "+": return a + b;
    case "-": return a - b;
    case "×":
    case "*": return a * b;
    case "÷":
    case "/": return b === 0 ? Infinity : a / b;
    default:  return b;
  }
};

export default function SimpleCalc({ triggerClassName = "" }) {
  const { user } = useAuth();
  const historyKey = `pd-calc-history:${user?.email || user?.id || "anon"}`;

  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [display, setDisplay] = useState("0");
  const [previous, setPrevious] = useState(null); // number | null
  const [op, setOp] = useState(null); // "+" | "-" | "×" | "÷" | null
  const [waiting, setWaiting] = useState(false); // ¿esperando el segundo operando?
  const [lastExpr, setLastExpr] = useState(""); // p.ej. "12 × 3 ="
  const [history, setHistory] = useState([]); // [{expr, result, at}]
  const rootRef = useRef(null);

  // Cargar historial cuando cambia el usuario
  useEffect(() => {
    try {
      const raw = localStorage.getItem(historyKey);
      if (raw) setHistory(JSON.parse(raw).slice(0, 5));
      else setHistory([]);
    } catch {
      setHistory([]);
    }
  }, [historyKey]);

  const pushHistory = (expr, result) => {
    const entry = { expr, result: String(result), at: Date.now() };
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, 5);
      try { localStorage.setItem(historyKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    try { localStorage.removeItem(historyKey); } catch { /* ignore */ }
    toast.success("Historial borrado");
  };

  const copyValue = async (txt) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(txt);
      } else {
        const ta = document.createElement("textarea");
        ta.value = txt; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success(`Copiado: ${txt}`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const reuseHistoryValue = (val) => {
    setDisplay(String(val).replace(/,/g, ""));
    setPrevious(null); setOp(null); setWaiting(false); setLastExpr("");
    setShowHistory(false);
  };

  const inputDigit = (d) => {
    if (waiting) {
      setDisplay(String(d));
      setWaiting(false);
      return;
    }
    setDisplay((cur) => (cur === "0" ? String(d) : cur + String(d)));
  };

  const inputDot = () => {
    if (waiting) {
      setDisplay("0.");
      setWaiting(false);
      return;
    }
    setDisplay((cur) => (cur.includes(".") ? cur : cur + "."));
  };

  const clearAll = () => {
    setDisplay("0"); setPrevious(null); setOp(null); setWaiting(false); setLastExpr("");
  };

  const delOne = () => {
    if (waiting) return;
    setDisplay((cur) => (cur.length > 1 ? cur.slice(0, -1) : "0"));
  };

  const toggleSign = () => {
    setDisplay((cur) => (cur.startsWith("-") ? cur.slice(1) : cur === "0" ? cur : "-" + cur));
  };

  const percent = () => {
    setDisplay((cur) => {
      const v = parseFloat(cur);
      return Number.isFinite(v) ? String(v / 100) : cur;
    });
  };

  const chooseOp = (nextOp) => {
    const inputValue = parseFloat(display);
    if (previous == null) {
      setPrevious(inputValue);
    } else if (op && !waiting) {
      const result = applyOp(previous, inputValue, op);
      setPrevious(result);
      setDisplay(fmt(result));
    }
    setOp(nextOp);
    setWaiting(true);
    setLastExpr("");
  };

  const equals = () => {
    if (op == null || previous == null) return;
    const inputValue = parseFloat(display);
    const result = applyOp(previous, inputValue, op);
    const expr = `${fmt(previous)} ${op} ${fmt(inputValue)} =`;
    setLastExpr(expr);
    setDisplay(fmt(result));
    setPrevious(null); setOp(null); setWaiting(true);
    if (Number.isFinite(result)) pushHistory(expr, fmt(result));
  };

  // Teclado físico
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const k = e.key;
      if (/^[0-9]$/.test(k)) { inputDigit(parseInt(k, 10)); e.preventDefault(); return; }
      if (k === ".") { inputDot(); e.preventDefault(); return; }
      if (k === "+" || k === "-") { chooseOp(k); e.preventDefault(); return; }
      if (k === "*") { chooseOp("×"); e.preventDefault(); return; }
      if (k === "/") { chooseOp("÷"); e.preventDefault(); return; }
      if (k === "Enter" || k === "=") { equals(); e.preventDefault(); return; }
      if (k === "Backspace") { delOne(); e.preventDefault(); return; }
      if (k === "Escape") { clearAll(); e.preventDefault(); return; }
      if (k === "%") { percent(); e.preventDefault(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, display, previous, op, waiting]);

  const Btn = ({ children, onClick, cls = "", tid, span = 1 }) => (
    <button
      onClick={onClick}
      className={`h-11 rounded-lg text-base font-semibold active:scale-95 transition-transform ${cls}`}
      style={{ gridColumn: `span ${span}` }}
      data-testid={tid}
    >
      {children}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`hidden lg:inline-flex items-center justify-center text-cyan-100 bg-white/10 rounded-lg w-7 h-7 border border-white/10 hover:bg-white/20 hover:text-white transition-colors ${triggerClassName}`}
          title="Calculadora"
          data-testid="simple-calc-trigger"
        >
          <Calculator className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[280px] p-0 rounded-2xl border border-slate-200 shadow-2xl overflow-hidden"
        data-testid="simple-calc-popover"
      >
        <div ref={rootRef} className="relative">
          {/* Header + display: gradiente PD Travel */}
          <div className="bg-gradient-to-br from-[#00157F] to-[#0037FF] px-4 pt-3 pb-4">
            <div className="flex items-center justify-between text-cyan-200 mb-1">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-cyan-200">
                <Calculator className="w-3 h-3" /> Calculadora
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => copyValue(display)}
                  className="text-cyan-200 hover:text-white transition-colors"
                  title="Copiar resultado al portapapeles"
                  data-testid="sc-copy"
                >
                  <Clipboard className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setShowHistory((v) => !v)}
                  className={`transition-colors ${showHistory ? "text-white" : "text-cyan-200 hover:text-white"}`}
                  title="Historial (últimas 5)"
                  data-testid="sc-history-toggle"
                >
                  <History className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div
              className="text-right text-4xl font-light text-white tabular-nums truncate"
              data-testid="simple-calc-display"
              title={display}
            >
              {display}
            </div>
            {op && !waiting && (
              <div className="text-right text-[10px] text-[#04CCDA] mt-0.5">
                {fmt(previous)} {op} …
              </div>
            )}
            {!op && lastExpr && (
              <div className="text-right text-[10px] text-cyan-300 mt-0.5 truncate" title={lastExpr}>
                {lastExpr}
              </div>
            )}
          </div>

          {/* Panel de historial (overlay dentro del popover) */}
          {showHistory && (
            <div className="absolute inset-0 bg-white z-10 flex flex-col" data-testid="sc-history-panel">
              <div className="bg-gradient-to-br from-[#00157F] to-[#0037FF] px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-white text-sm font-semibold">
                  <History className="w-4 h-4" /> Últimas operaciones
                </div>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-cyan-200 hover:text-white"
                  data-testid="sc-history-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto max-h-[360px] divide-y divide-slate-100">
                {history.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-400">
                    Sin operaciones aún.
                    <p className="text-[11px] mt-1 text-slate-400">Cada resultado se guardará aquí automáticamente.</p>
                  </div>
                ) : (
                  history.map((h, i) => (
                    <div
                      key={h.at}
                      className="flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 group"
                      data-testid={`sc-history-item-${i}`}
                    >
                      <button
                        onClick={() => reuseHistoryValue(h.result)}
                        className="flex-1 text-left min-w-0"
                        title="Usar este resultado en la calculadora"
                      >
                        <p className="text-[11px] text-slate-400 truncate">{h.expr}</p>
                        <p className="text-base font-semibold text-[#00157F] tabular-nums truncate">{h.result}</p>
                      </button>
                      <button
                        onClick={() => copyValue(h.result)}
                        className="ml-2 p-1.5 rounded-md text-slate-400 hover:text-[#0037FF] hover:bg-blue-50 transition-colors"
                        title="Copiar al portapapeles"
                        data-testid={`sc-history-copy-${i}`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
              {history.length > 0 && (
                <div className="border-t border-slate-200 p-2 bg-slate-50">
                  <button
                    onClick={clearHistory}
                    className="w-full text-[11px] text-slate-500 hover:text-red-600 py-1.5"
                    data-testid="sc-history-clear"
                  >
                    Borrar historial
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Keypad */}
          <div className="grid grid-cols-4 gap-1.5 p-2.5 bg-slate-50">
            <Btn onClick={clearAll} cls="bg-slate-200 text-slate-700 hover:bg-slate-300" tid="sc-ac">AC</Btn>
            <Btn onClick={toggleSign} cls="bg-slate-200 text-slate-700 hover:bg-slate-300" tid="sc-sign">±</Btn>
            <Btn onClick={percent} cls="bg-slate-200 text-slate-700 hover:bg-slate-300" tid="sc-pct">%</Btn>
            <Btn onClick={() => chooseOp("÷")} cls={`text-white hover:opacity-90 ${op === "÷" && waiting ? "bg-white text-[#04CCDA] ring-2 ring-[#04CCDA]" : "bg-[#04CCDA]"}`} tid="sc-div">÷</Btn>

            <Btn onClick={() => inputDigit(7)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-7">7</Btn>
            <Btn onClick={() => inputDigit(8)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-8">8</Btn>
            <Btn onClick={() => inputDigit(9)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-9">9</Btn>
            <Btn onClick={() => chooseOp("×")} cls={`text-white hover:opacity-90 ${op === "×" && waiting ? "bg-white text-[#04CCDA] ring-2 ring-[#04CCDA]" : "bg-[#04CCDA]"}`} tid="sc-mul">×</Btn>

            <Btn onClick={() => inputDigit(4)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-4">4</Btn>
            <Btn onClick={() => inputDigit(5)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-5">5</Btn>
            <Btn onClick={() => inputDigit(6)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-6">6</Btn>
            <Btn onClick={() => chooseOp("-")} cls={`text-white hover:opacity-90 ${op === "-" && waiting ? "bg-white text-[#04CCDA] ring-2 ring-[#04CCDA]" : "bg-[#04CCDA]"}`} tid="sc-sub">−</Btn>

            <Btn onClick={() => inputDigit(1)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-1">1</Btn>
            <Btn onClick={() => inputDigit(2)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-2">2</Btn>
            <Btn onClick={() => inputDigit(3)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-3">3</Btn>
            <Btn onClick={() => chooseOp("+")} cls={`text-white hover:opacity-90 ${op === "+" && waiting ? "bg-white text-[#04CCDA] ring-2 ring-[#04CCDA]" : "bg-[#04CCDA]"}`} tid="sc-add">+</Btn>

            <Btn onClick={() => inputDigit(0)} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-0" span={2}>0</Btn>
            <Btn onClick={inputDot} cls="bg-white text-slate-800 hover:bg-slate-100 border border-slate-200" tid="sc-dot">.</Btn>
            <Btn onClick={equals} cls="bg-[#0037FF] text-white hover:bg-[#00157F]" tid="sc-eq">=</Btn>
          </div>

          {/* Tip fila */}
          <div className="bg-slate-100 border-t border-slate-200 px-3 py-1.5 text-[10px] text-slate-500 flex items-center justify-between">
            <span>Teclado: 0-9 · + − × ÷ · Enter =</span>
            <button
              onClick={delOne}
              className="inline-flex items-center gap-1 text-slate-600 hover:text-[#0037FF]"
              data-testid="sc-del"
              title="Borrar último (Backspace)"
            >
              <Delete className="w-3.5 h-3.5" /> DEL
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
